/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { DefinitionRendererBuilder } from './ParameterDefinitionRenderer';
import { ParameterDefinition, ParameterSection } from '@karya/parameter-specs';
import { UseFormReturn } from '../hooks';
import _ from 'lodash';
import { FormRendererConfig } from './FormRenderer';

export interface SectionRendererProps {
  ctx: UseFormReturn<any>['ctx'];
  section: ParameterSection<any>;
  index?: number;
  isLast?: boolean;
  showStepper?: boolean;
}
export function SectionRendererBuilder(config: FormRendererConfig) {
  const { SectionContainer } = config.components;
  const DefinitionRenderer = DefinitionRendererBuilder(config.components);
  return React.memo(
    (props: SectionRendererProps) => {
      return (
        <SectionContainer
          label={props.section.label}
          description={props.section.description}
          index={props.index}
          isLast={props.isLast}
          showStepper={props.showStepper}
        >
          {props.section.parameters.map((param) => (
            <DefinitionRenderer parameter={param} ctx={props.ctx} key={param.id} />
          ))}
        </SectionContainer>
      );
    },
    (prev, next) => {
      if (prev === next) return true;

      if (!_.isEqual(prev.section, next.section)) {
        return false;
      }

      const isSectionChanged = prev.section.parameters.some(
        (def: Extract<ParameterDefinition<string>, { requires: Array<unknown> }>) => {
          if (def.type === 'json_array' && def.items) {
            return true;
          }
          const id = def.id as string;
          const valueChanged = !_.isEqual(prev.ctx.form[id], next.ctx.form[id]);
          const errorChanged = !_.isEqual(prev.ctx.errors[id], next.ctx.errors[id]);
          const requirementChanged = def.requires?.some(([key]) => prev.ctx.form[key] != next.ctx.form[key]);
          return valueChanged || errorChanged || requirementChanged;
        },
      );

      if (isSectionChanged) {
        return false;
      }

      const customRendererUsed = prev.section.parameters.some((def) => def.renderer !== undefined);
      if (customRendererUsed) {
        return false;
      }

      return true;
    },
  );
}
